import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import i18n from '../../i18n';
import { pathFor, alternatePath, type Lang } from '../../router/paths';

export default function Navigation() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [lang, setLang] = useState<'de' | 'en'>(i18n.language === 'en' ? 'en' : 'de');
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const toggleLang = () => {
    const next: Lang = lang === 'de' ? 'en' : 'de';
    const nextPath = alternatePath(location.pathname, next);
    i18n.changeLanguage(next);
    setLang(next);
    if (nextPath !== location.pathname) navigate(nextPath);
  };

  const homePath = pathFor('home', lang);

  const scrollToSection = (id: string) => {
    if (location.pathname !== homePath) {
      navigate(homePath);
      setTimeout(() => {
        const el = document.getElementById(id);
        if (el) window.scrollTo({ top: el.getBoundingClientRect().top + window.pageYOffset - 80, behavior: 'smooth' });
      }, 100);
    } else {
      const el = document.getElementById(id);
      if (el) window.scrollTo({ top: el.getBoundingClientRect().top + window.pageYOffset - 80, behavior: 'smooth' });
    }
    setIsMobileMenuOpen(false);
  };

  const navigateTo = (path: string) => { navigate(path); window.scrollTo({ top: 0, behavior: 'instant' }); setIsMobileMenuOpen(false); };

  const linkClass = `text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
    isScrolled ? 'text-gray-700 hover:text-amber-700' : 'text-white hover:text-amber-200'
  }`;

  const navItems = [
    { label: t('navRooms'),         action: () => navigateTo(pathFor('rooms', lang)) },
    { label: t('navHotel'),         action: () => navigateTo(pathFor('hotel', lang)) },
    { label: t('navLocation'),      action: () => scrollToSection('lage') },
    { label: t('navContact'),       action: () => scrollToSection('kontakt') },
    { label: t('navAusflugsziele'), action: () => navigateTo(pathFor('ausflugsziele', lang)) },
  ];

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? 'bg-white shadow-md' : 'bg-transparent'}`}>
      <div className="max-w-7xl mx-auto px-4 lg:px-8">
        <div className="flex items-center justify-between h-14 lg:h-20">

          <div className="flex items-center cursor-pointer" onClick={() => { navigate(homePath); window.scrollTo({ top: 0, behavior: 'smooth' }); }}>
            <img
              src="/images/brand/logo_hotel_alpenhof.png"
              alt="Hotel Alpenhof Gauting Logo"
              className="h-14 lg:h-24 w-auto"
              style={{ marginTop: '4px', marginBottom: '-8px' }}
            />
          </div>

          {/* Desktop */}
          <div className="hidden lg:flex items-center space-x-7">
            {navItems.map(({ label, action }) => (
              <button key={label} onClick={action} className={linkClass}>
                {label}
              </button>
            ))}

            {/* Sprachauswahl */}
            <button
              onClick={toggleLang}
              className={`text-xs font-semibold tracking-widest border rounded px-2 py-1 transition-colors cursor-pointer ${
                isScrolled
                  ? 'border-gray-300 text-gray-500 hover:border-amber-700 hover:text-amber-700'
                  : 'border-white/50 text-white/80 hover:border-white hover:text-white'
              }`}
            >
              {lang === 'de' ? 'EN' : 'DE'}
            </button>

            <button
              className="d21-trigger-ibe bg-amber-700 text-white px-6 py-2.5 rounded-md text-sm font-medium hover:bg-amber-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              {t('navBook')}
            </button>
          </div>

          {/* MOBILE: Hotel-Name zentriert zwischen Logo und Burger */}
          <span className={`lg:hidden text-xs font-semibold tracking-widest uppercase ${isScrolled ? 'text-gray-500' : 'text-white/80'}`}>
            {t('hotelName')}
          </span>

          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            aria-label="Menü"
            className="lg:hidden cursor-pointer w-11 h-11 flex items-center justify-center -mr-2"
          >
            <i className={`ri-menu-line text-2xl ${isScrolled ? 'text-gray-700' : 'text-white'}`}></i>
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="lg:hidden bg-white border-t border-gray-200">
          <div className="px-6 py-4">
            {/* Sprachwechsel oben rechts */}
            <div className="flex justify-end pb-2">
              <button
                onClick={toggleLang}
                aria-label={`Sprache wechseln zu ${lang === 'de' ? 'English' : 'Deutsch'}`}
                className="text-xs font-semibold tracking-widest uppercase text-amber-700 hover:text-amber-600 transition-colors cursor-pointer py-2 px-3 -mr-3"
              >
                {lang === 'de' ? 'English' : 'Deutsch'}
              </button>
            </div>
            <div className="space-y-1">
              {navItems.map(({ label, action }) => (
                <button
                  key={label}
                  onClick={action}
                  className="block w-full text-left text-sm font-medium text-gray-700 hover:text-amber-700 cursor-pointer py-3 min-h-11"
                >
                  {label}
                </button>
              ))}
              <button
                className="d21-trigger-ibe block w-full bg-amber-700 text-white px-6 py-3 rounded-md text-sm font-medium hover:bg-amber-600 transition-colors cursor-pointer mt-3"
              >
                {t('navBook')}
              </button>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}
